<template>
  <PaperContainer
    class="flex h-full flex-col gap-4 py-4 lg:gap-8 lg:overflow-hidden lg:py-8"
  >
    <div class="px-4 lg:px-8">
      <slot name="title">
        <component
          :is="component"
          v-if="component"
          :title="title ?? t('page-title')"
        />
        <ViewTitle
          v-else
          :provider-key="logoProviderKey"
          :title="title ?? t('page-title')"
        />
      </slot>
    </div>

    <div v-if="pageMessage" class="px-4 lg:px-8">
      <UIMessage :severity="pageMessage.severity" :display-icon="false">
        <i18n-t :keypath="pageMessage.key" />
      </UIMessage>
    </div>

    <div class="flex grow flex-col space-y-6 px-4 lg:overflow-y-auto lg:px-8">
      <slot name="default" />
    </div>

    <div class="px-4 lg:px-8">
      <component
        :is="providerBankTag"
        v-if="providerBankTag && providerKey"
        :provider-key="providerKey"
      />
      <slot name="footer">
        <slot name="disclosure" />
        <PageFooter>
          <template #back>
            <slot name="back" />
          </template>

          <template #next>
            <slot name="next" />
          </template>
        </PageFooter>
      </slot>
    </div>
  </PaperContainer>
</template>

<script setup lang="ts">
  import type { UIMessageProps } from "@ui-kit/components/content"
  import {
    useLocalizedComponents,
    ViewTitle,
  } from "@versatile-credit-apply/components"
  import { PaperContainer } from "@versatile-credit-apply/components/container"

  import { useCurrentProviderKey } from "@versatile-credit-apply/composition"
  import type { ProviderKey } from "@versatile-credit-apply/omni-apply-client"
  import { useRoute } from "vue-router"

  import PageFooter from "./PageFooter.vue"
  import { useI18n } from "vue-i18n"
  import { computed } from "vue"

  interface Slots {
    /**
     * Where your content is rendered
     */
    default(): never

    /**
     * Where your footer content is rendered
     */
    footer(): never

    /**
     * Render your back button if you want
     */
    back(): never

    /**
     * Render your next button if you want
     */
    next(): never

    /**
     * Render your title if you want
     */
    title(): never

    disclosure(): never
  }

  defineSlots<Slots>()

  interface Props {
    /**
     * Allow override of the current provider key
     */
    providerKey?: ProviderKey

    /**
     * Allow override of the current title
     */
    title?: string

    /**
     * Determines if this is a screen that the Provider logo should be hidden
     */
    hideProviderLogo?: boolean
  }

  const props = defineProps<Props>()

  const { t } = useI18n({
    useScope: "parent",
  })

  const currentProviderKey = useCurrentProviderKey()
  const providerKey = computed(
    () => props.providerKey ?? currentProviderKey.value
  )

  const route = useRoute()

  interface PageMessage {
    severity: UIMessageProps["severity"]
    key: string
  }

  const pageMessage = computed<PageMessage | undefined>(() => {
    const warning = route?.query?.warning
    const error = route?.query?.error

    if (warning && typeof warning === "string") {
      return {
        severity: "warn",
        key: warning,
      }
    }

    if (error && typeof error === "string") {
      return {
        severity: "error",
        key: error,
      }
    }

    return undefined
  })

  const component = useLocalizedComponents(providerKey, {
    tdbank_standard: () =>
      import("@/providers/tdbank_standard/components/TdBankTitle.vue"),
  })

  const providerBankTag = useLocalizedComponents(providerKey, {
    snap_standard: () =>
      import("@/providers/snap_standard/components/SnapBankLoanTag.vue"),
  })

  const logoProviderKey = computed(() => {
    if (props.hideProviderLogo) {
      return undefined
    }
    return providerKey.value ?? undefined
  })
</script>
